// Levi character · shared component · Feb 2026 · iter53.
//
// Renders Levi at one of four fidelity levels using ONLY the flat raster
// poses already commissioned by Rio:
//   • /levi/levi-detective.webp — active pose, magnifying glass out (used
//     for the "search / sniff" stage of the Meet Levi intro).
//   • /levi/levi-sitting.webp   — ready pose, seated + waving (used for
//     the settled idle state, the launcher, and the reduced-motion path).
//   • /levi/levi-badge.webp     — circular sticker (compact avatar).
//
// **Asset limitation:** we do not currently have a walk-cycle sprite or
// Lottie/Rive file. The Meet Levi intro therefore uses a restrained
// cross-fade + subtle horizontal slide between the two DRAWN poses
// instead of warping a single static PNG. The animation is honest — it
// does not pretend Levi has walking artwork he doesn't have.
//
// Modes:
//   • "meet-page"     · plays the staged intro once then holds seated idle.
//   • "launcher"      · seated idle only; subtle breathing/tail motion.
//   • "static"        · zero animation (used when prefers-reduced-motion
//     is true OR when an <img> onError falls back).
//
// Zero LLM, zero API. Contractor-facing only.

import { useEffect, useState } from "react";

const POSES = {
  detective: "/levi/levi-detective.webp",
  sitting:   "/levi/levi-sitting.webp",
  badge:     "/levi/levi-badge.webp",
  // Approved 2026-08-22 · seated coworker portrait (JobClose polo, cap,
  // Levi name tag, at the estimating desk). Used exclusively by the
  // modernized Levi conversation drawer header. Additive — no existing
  // pose asset is overwritten.
  coworker:  "/brand/levi/levi-coworker.png",
};

// Meet-page intro stages (ms offsets from mount).
const STAGES = {
  walkIn:    0,      // slide-in with detective pose
  sniff:     900,    // small vertical bob to signal sniffing
  pause:     2600,
  sit:       3200,   // cross-fade to sitting pose
  ready:     4600,   // wag-in begins
};

const usePrefersReducedMotion = () => {
  const [reduced, setReduced] = useState(false);
  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return;
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    const apply = () => setReduced(mq.matches);
    apply();
    mq.addEventListener?.("change", apply);
    return () => mq.removeEventListener?.("change", apply);
  }, []);
  return reduced;
};

export default function LeviCharacter({
  mode = "static",
  size = 192,
  className = "",
  alt = "Levi, your coworker at JobClose AI",
  testid,
}) {
  const prefersReduced = usePrefersReducedMotion();
  const [stage, setStage] = useState(mode === "meet-page" ? "walkIn" : "ready");
  const [staticFallback, setStaticFallback] = useState(false);

  useEffect(() => {
    if (mode !== "meet-page" || prefersReduced) return;
    const timers = [];
    timers.push(setTimeout(() => setStage("sniff"),  STAGES.sniff));
    timers.push(setTimeout(() => setStage("pause"),  STAGES.pause));
    timers.push(setTimeout(() => setStage("sit"),    STAGES.sit));
    timers.push(setTimeout(() => setStage("ready"),  STAGES.ready));
    return () => timers.forEach(clearTimeout);
  }, [mode, prefersReduced]);

  // Reduced-motion + static modes always render the sitting pose in place.
  const isStatic = mode === "static" || prefersReduced || staticFallback;

  // Coworker portrait — the approved seated Levi at the estimating desk.
  // Used exclusively by the modernized Levi conversation drawer header.
  if (mode === "coworker") {
    return (
      <img
        src={POSES.coworker}
        alt={alt}
        loading="lazy"
        data-testid={testid || "levi-character-coworker"}
        data-levi-mode="coworker"
        className={`object-cover select-none ${className}`}
        onError={() => setStaticFallback(true)}
      />
    );
  }

  if (isStatic) {
    return (
      <img
        src={POSES.sitting}
        alt={alt}
        width={size}
        height={size}
        loading="lazy"
        data-testid={testid || "levi-character-static"}
        data-levi-mode={mode}
        data-levi-static-fallback={staticFallback ? "1" : "0"}
        className={`object-contain select-none ${className}`}
        onError={() => setStaticFallback(true)}
      />
    );
  }

  // Launcher mode: single sitting pose with a gentle breathing loop.
  if (mode === "launcher") {
    return (
      <img
        src={POSES.sitting}
        alt={alt}
        width={size}
        height={size}
        loading="lazy"
        data-testid={testid || "levi-character-launcher"}
        data-levi-mode="launcher"
        className={`object-contain select-none jc-levi-launcher-idle ${className}`}
        onError={() => setStaticFallback(true)}
      />
    );
  }

  // Meet-page staged intro. Both poses are rendered stacked; CSS transitions
  // fade / translate between them. Neither pose is distorted — the effect
  // is authentic to the drawn artwork.
  const walkingIn  = stage === "walkIn";
  const sniffing   = stage === "sniff";
  const paused     = stage === "pause";
  const sittingNow = stage === "sit" || stage === "ready";
  const wagging    = stage === "ready";

  return (
    <div
      className={`relative overflow-hidden ${className}`}
      style={{ width: size, height: size }}
      data-testid={testid || "levi-character-meet"}
      data-levi-mode="meet-page"
      data-levi-stage={stage}
    >
      {/* Detective pose · active / searching. Slides in from the left,
          then a subtle vertical bob during the sniff stage. */}
      <img
        src={POSES.detective}
        alt=""
        aria-hidden="true"
        width={size}
        height={size}
        loading="eager"
        data-testid="levi-character-detective-layer"
        className={[
          "absolute inset-0 object-contain transition-all duration-700 ease-out select-none",
          walkingIn ? "opacity-100 translate-x-[-14%]" : "",
          sniffing  ? "opacity-100 translate-x-0 jc-levi-sniff" : "",
          paused    ? "opacity-100 translate-x-0 jc-levi-head-tilt" : "",
          sittingNow ? "opacity-0 translate-x-0" : "",
        ].join(" ")}
        onError={() => setStaticFallback(true)}
      />

      {/* Sitting pose · calm, seated, waving. Fades in for the sit + ready
          stages; the wag animation is a very subtle rotate + scale
          oscillation applied only in the ready stage. Slice 2F1 adds an
          occasional blink so Levi feels calmly alive without becoming
          distracting; the blink is silenced by prefers-reduced-motion. */}
      <img
        src={POSES.sitting}
        alt={alt}
        width={size}
        height={size}
        loading="eager"
        data-testid="levi-character-sitting-layer"
        className={[
          "absolute inset-0 object-contain transition-opacity duration-700 ease-out select-none",
          sittingNow ? "opacity-100" : "opacity-0",
          wagging   ? "jc-levi-wag-idle jc-levi-blink" : "",
        ].join(" ")}
        onError={() => setStaticFallback(true)}
      />
    </div>
  );
}

export { POSES as LEVI_POSES };
